"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import {
  createBranchService,
  listBranchesService,
  setBranchStatusService,
  updateBranchService,
} from "@/features/church/config/branches/services/branches.service"
import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import {
  createSlug,
  generateBranchId,
  generateServiceId,
  hasDuplicateName,
} from "@/features/church/config/branches/store/branches-store.helpers"
import {
  BranchServiceStatus,
  BranchStatus,
  BranchType,
  type Branch,
  type CreateBranchInput,
  type UpdateBranchInput,
} from "@/features/church/config/branches/types/branch.types"

const INITIAL_BRANCHES: Branch[] = []
const NAME_MIN_LENGTH = 2
const NAME_MAX_LENGTH = 120

type BranchesState = {
  branches: Branch[]
  isLoading: boolean
  listError: unknown
  actionError: unknown
  lastLoadedWorkspaceId: string | null
  fetchBranches: (options?: { workspaceId?: string; force?: boolean }) => Promise<Branch[]>
  createBranch: (payload: CreateBranchInput) => Promise<Branch>
  updateBranch: (id: string, payload: UpdateBranchInput) => Promise<Branch>
  setBranchStatus: (id: string, status: BranchStatus) => Promise<Branch>
  addBranchService: (branchId: string, name: string) => Branch
  updateBranchService: (branchId: string, serviceId: string, name: string) => Branch
  setBranchServiceStatus: (
    branchId: string,
    serviceId: string,
    status: BranchServiceStatus
  ) => Branch
  deleteBranchService: (branchId: string, serviceId: string) => Branch
  getBranchById: (id: string) => Branch | undefined
}

function normalizeOptionalText(value: string | undefined) {
  if (value === undefined) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : ""
}

export const useBranchesStore = create<BranchesState>()(
  persist(
    (set, get) => ({
  branches: INITIAL_BRANCHES,
  isLoading: false,
  listError: null,
  actionError: null,
  lastLoadedWorkspaceId: null,

  fetchBranches: async (options = {}) => {
    const workspaceId = options.workspaceId ?? getAuthContextState().activeWorkspaceId ?? null
    if (!workspaceId) {
      set({ branches: [], listError: new Error("errors.noWorkspace"), lastLoadedWorkspaceId: null })
      return []
    }

    if (!options.force && get().lastLoadedWorkspaceId === workspaceId) {
      return get().branches
    }

    set({ isLoading: true, listError: null })

    try {
      const remoteBranches = await listBranchesService({ workspaceId })
      const branches: Branch[] = remoteBranches.map((branch) => ({
        id: branch.id,
        name: branch.name,
        code: branch.code ?? null,
        slug: branch.slug ?? createSlug(branch.name),
        details: branch.details,
        phoneNumber: branch.phoneNumber,
        branchType: branch.branchType ?? BranchType.Normal,
        services: [],
        principalMemberId: branch.principalMemberId ?? null,
        principalMember: branch.principalMember ?? null,
        customFieldValues: {},
        language: branch.language,
        timezone: branch.timezone,
        status: branch.status ?? BranchStatus.ACTIVE,
        membersCount: branch.membersCount,
        teamCount: branch.teamCount,
        memberPreview: branch.memberPreview,
        createdAt: branch.createdAt ?? new Date().toISOString(),
        updatedAt: branch.updatedAt ?? new Date().toISOString(),
      }))

      set({
        branches,
        isLoading: false,
        listError: null,
        lastLoadedWorkspaceId: workspaceId,
      })

      return branches
    } catch (error) {
      set({ isLoading: false, listError: error })
      throw error
    }
  },

  getBranchById: (id) => get().branches.find((branch) => branch.id === id),

  createBranch: async (payload) => {
    const name = payload.name.trim()
    if (name.length < NAME_MIN_LENGTH) {
      throw new Error("errors.nameMin")
    }
    if (name.length > NAME_MAX_LENGTH) {
      throw new Error("errors.nameMax")
    }
    if (hasDuplicateName(get().branches, name)) {
      throw new Error("errors.nameUnique")
    }

    set({ isLoading: true, actionError: null })

    try {
      const createdRemote = await createBranchService(payload)
      const details = normalizeOptionalText(payload.details)
      const phoneNumber = normalizeOptionalText(payload.phoneNumber)

      const now = new Date().toISOString()
      const created: Branch = {
        id: createdRemote.id ?? generateBranchId(),
        name: createdRemote.name ?? name,
        code: createdRemote.code ?? payload.code?.trim() ?? null,
        slug: createdRemote.slug ?? createSlug(name),
        details: createdRemote.details ?? (details || undefined),
        phoneNumber: createdRemote.phoneNumber ?? (phoneNumber || undefined),
        branchType: createdRemote.branchType ?? payload.branchType ?? BranchType.Normal,
        services: [],
        principalMemberId: createdRemote.principalMemberId ?? payload.principalMemberId ?? null,
        principalMember: createdRemote.principalMember ?? null,
        customFieldValues: payload.customFieldValues ?? {},
        language: createdRemote.language ?? payload.language,
        timezone: createdRemote.timezone ?? payload.timezone,
        status: createdRemote.status ?? payload.status ?? BranchStatus.ACTIVE,
        createdAt: createdRemote.createdAt ?? now,
        updatedAt: createdRemote.updatedAt ?? now,
      }

      set((state) => ({
        branches: [created, ...state.branches],
        isLoading: false,
        actionError: null,
      }))

      return created
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  updateBranch: async (id, payload) => {
    const existing = get().branches.find((branch) => branch.id === id)
    if (!existing) {
      throw new Error("errors.notFound")
    }

    const nextName = payload.name?.trim() ?? existing.name
    if (payload.name !== undefined && nextName.length < NAME_MIN_LENGTH) {
      throw new Error("errors.nameMin")
    }
    if (payload.name !== undefined && nextName.length > NAME_MAX_LENGTH) {
      throw new Error("errors.nameMax")
    }
    if (payload.name !== undefined && hasDuplicateName(get().branches, nextName, id)) {
      throw new Error("errors.nameUnique")
    }

    const details = normalizeOptionalText(payload.details)
    const phoneNumber = normalizeOptionalText(payload.phoneNumber)

    const patch: UpdateBranchInput = {}
    if (payload.name !== undefined && nextName !== existing.name) {
      patch.name = nextName
    }
    if (payload.branchType !== undefined && payload.branchType !== existing.branchType) {
      patch.branchType = payload.branchType
    }
    if (payload.details !== undefined && details !== (existing.details ?? "")) {
      patch.details = details
    }
    if (payload.phoneNumber !== undefined && phoneNumber !== (existing.phoneNumber ?? "")) {
      patch.phoneNumber = phoneNumber
    }
    if (payload.principalMemberId !== undefined && payload.principalMemberId !== existing.principalMemberId) {
      patch.principalMemberId = payload.principalMemberId
    }
    if (payload.status !== undefined && payload.status !== existing.status) {
      patch.status = payload.status
    }
    if (payload.customFieldValues !== undefined) {
      patch.customFieldValues = payload.customFieldValues
    }

    if (Object.keys(patch).length === 0) {
      return existing
    }

    set({ isLoading: true, actionError: null })

    try {
      const updatedRemote = await updateBranchService(id, patch)
      const principalChanged = patch.principalMemberId !== undefined
      const nextPrincipalId = principalChanged ? patch.principalMemberId ?? null : existing.principalMemberId

      const updated: Branch = {
        ...existing,
        name: updatedRemote.name ?? nextName,
        code: updatedRemote.code ?? existing.code ?? null,
        slug: updatedRemote.slug ?? existing.slug,
        details:
          updatedRemote.details ??
          (patch.details !== undefined ? patch.details || undefined : existing.details),
        phoneNumber:
          updatedRemote.phoneNumber ??
          (patch.phoneNumber !== undefined ? patch.phoneNumber || undefined : existing.phoneNumber),
        branchType: updatedRemote.branchType ?? patch.branchType ?? existing.branchType,
        principalMemberId: updatedRemote.principalMemberId ?? nextPrincipalId,
        principalMember:
          updatedRemote.principalMember ??
          (principalChanged
            ? nextPrincipalId
              ? existing.principalMember?.id === nextPrincipalId
                ? existing.principalMember
                : null
              : null
            : existing.principalMember),
        customFieldValues: patch.customFieldValues ?? existing.customFieldValues ?? {},
        language: updatedRemote.language ?? patch.language ?? existing.language,
        timezone: updatedRemote.timezone ?? patch.timezone ?? existing.timezone,
        status: updatedRemote.status ?? patch.status ?? existing.status,
        updatedAt: updatedRemote.updatedAt ?? new Date().toISOString(),
      }

      set((state) => ({
        branches: state.branches.map((branch) => (branch.id === id ? updated : branch)),
        isLoading: false,
        actionError: null,
      }))

      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  setBranchStatus: async (id, status) => {
    const existing = get().branches.find((branch) => branch.id === id)
    if (!existing) {
      throw new Error("errors.notFound")
    }

    set({ isLoading: true, actionError: null })

    try {
      const updatedRemote = await setBranchStatusService(id, status)

      const updated: Branch = {
        ...existing,
        status: updatedRemote.status ?? status,
        updatedAt: updatedRemote.updatedAt ?? new Date().toISOString(),
      }

      set((state) => ({
        branches: state.branches.map((branch) => (branch.id === id ? updated : branch)),
        isLoading: false,
        actionError: null,
      }))

      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  addBranchService: (branchId, name) => {
    const branch = get().branches.find((item) => item.id === branchId)
    if (!branch) {
      throw new Error("errors.notFound")
    }

    const nextName = name.trim()
    const now = new Date().toISOString()
    const createdService = {
      id: generateServiceId(),
      name: nextName,
      status: BranchServiceStatus.ACTIVE,
      createdAt: now,
    }
    const updatedBranch = {
      ...branch,
      services: [createdService, ...branch.services],
      updatedAt: now,
    }

    set((state) => ({
      branches: state.branches.map((item) => (item.id === branchId ? updatedBranch : item)),
    }))

    return updatedBranch
  },

  updateBranchService: (branchId, serviceId, name) => {
    const branch = get().branches.find((item) => item.id === branchId)
    if (!branch) {
      throw new Error("errors.notFound")
    }

    const service = branch.services.find((item) => item.id === serviceId)
    if (!service) {
      throw new Error("errors.serviceNotFound")
    }

    const nextName = name.trim()
    const now = new Date().toISOString()
    const updatedBranch = {
      ...branch,
      services: branch.services.map((item) =>
        item.id === serviceId
          ? {
              ...item,
              name: nextName,
            }
          : item
      ),
      updatedAt: now,
    }

    set((state) => ({
      branches: state.branches.map((item) => (item.id === branchId ? updatedBranch : item)),
    }))

    return updatedBranch
  },

  setBranchServiceStatus: (branchId, serviceId, status) => {
    const branch = get().branches.find((item) => item.id === branchId)
    if (!branch) {
      throw new Error("errors.notFound")
    }

    const service = branch.services.find((item) => item.id === serviceId)
    if (!service) {
      throw new Error("errors.serviceNotFound")
    }

    const now = new Date().toISOString()
    const updatedBranch = {
      ...branch,
      services: branch.services.map((item) => (item.id === serviceId ? { ...item, status } : item)),
      updatedAt: now,
    }

    set((state) => ({
      branches: state.branches.map((item) => (item.id === branchId ? updatedBranch : item)),
    }))

    return updatedBranch
  },

  deleteBranchService: (branchId, serviceId) => {
    const branch = get().branches.find((item) => item.id === branchId)
    if (!branch) {
      throw new Error("errors.notFound")
    }

    const service = branch.services.find((item) => item.id === serviceId)
    if (!service) {
      throw new Error("errors.serviceNotFound")
    }

    const now = new Date().toISOString()
    const updatedBranch = {
      ...branch,
      services: branch.services.filter((item) => item.id !== serviceId),
      updatedAt: now,
    }

    set((state) => ({
      branches: state.branches.map((item) => (item.id === branchId ? updatedBranch : item)),
    }))

    return updatedBranch
  },
    }),
    {
      name: "branches-store-v2",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        branches: state.branches,
        lastLoadedWorkspaceId: state.lastLoadedWorkspaceId,
      }),
    }
  )
)
